import { Badge } from '@mui/material';
import { Tooltip } from 'antd';
import { filter, find, get, groupBy } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useChatMessage } from 'store/chat/selectors';
import styled from 'styled-components';
import cstmMsgSvg from 'utils/assets/images/cstm-msg.svg';

interface ChatMessageForBadge {
    receiverId?: string;
    seen?: boolean;
    brodcastId?: string | null;
    classId?: string | number;
    brodcastSeen?: Array<{ id?: string }>;
}

interface CurrentUserSlice {
    id?: string;
    role?: string;
}

export type ChatMenuUser = CurrentUserSlice;

interface CurrentClassSlice {
    id?: string | number;
}

function useChatMenuNavigationInternal(
    currentUser: CurrentUserSlice | null | undefined,
    currentClass: CurrentClassSlice | null | undefined
): { messageCounter: number; navigateChat: () => void } {
    const chatMessages = useChatMessage() as ChatMessageForBadge[] | undefined;
    const navigate = useNavigate();
    const location = useLocation();
    const [messageCounter, setMessageCounter] = useState(0);

    useEffect(() => {
        if (!get(currentUser, 'id')) {
            return;
        }
        let count = 0;
        if (chatMessages && chatMessages.length > 0) {
            count = (chatMessages || []).reduce((accumulator, c) => {
                if (
                    c.receiverId === get(currentUser, 'id') &&
                    !c.seen &&
                    !c.brodcastId &&
                    c.classId === currentClass?.id
                ) {
                    return accumulator + 1;
                }
                return accumulator;
            }, 0);
            const brodcastMessage = groupBy(chatMessages, 'brodcastId');
            if (get(currentUser, 'role') === 'student' && currentUser?.id) {
                const broadcastCounts: Record<string, number> = {};
                Object.keys(brodcastMessage).forEach((key) => {
                    if (key == null || !key || key === 'null') {
                        return;
                    }
                    broadcastCounts[key] = filter(
                        brodcastMessage[key],
                        (msg) => !find(msg.brodcastSeen, ['id', currentUser.id])
                    ).length;
                });
                const classId = get(currentClass, 'id') as string | number | undefined;
                const activeClassVal =
                    classId != null && broadcastCounts[String(classId)] > 0
                        ? broadcastCounts[String(classId)]
                        : 0;
                count += activeClassVal;
            }
        }
        setMessageCounter(count);
    }, [chatMessages, currentClass, currentUser]);

    const navigateChat = useCallback(() => {
        if (location.pathname === '/chat') {
            navigate(-1);
        } else {
            navigate('/chat');
        }
    }, [location.pathname, navigate]);

    return { messageCounter, navigateChat };
}

const MsgRowItem = styled.div`
    margin-right: 8px;
    @media (max-width: 1362px) {
        margin-right: 3px;
    }
    @media (max-width: 1099px) {
        margin-right: 12px;
    }
    @media (max-width: 1099px) {
        margin-right: 8px;
    }
`;

const MsgCustomBtn = styled.button`
    background-color: transparent;
    border: none;
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    outline: none;
    text-align: left;
    margin-right: 5px;
`;

function HeaderChatNavButton(): React.ReactElement | null {
    const currentUser = useSelector(
        (state: { currentUser?: CurrentUserSlice | null }) => state.currentUser
    );
    const currentClass = useSelector(
        (state: { currentClass?: CurrentClassSlice | null }) => state.currentClass
    );
    const location = useLocation();
    const isTeacherHome =
        location.pathname === '/teacher/home' ||
        location.pathname === '/student/home' ||
        location.pathname === '/parents-student-home';
    const { messageCounter, navigateChat } = useChatMenuNavigationInternal(currentUser, currentClass);

    if ((currentUser?.role !== 'teacher' && currentUser?.role !== 'student') || isTeacherHome) {
        return null;
    }
    return (
        <MsgRowItem style={{ cursor: 'pointer' }}>
            <Tooltip placement="bottom" title="Chat">
                <MsgCustomBtn type="button" onClick={navigateChat} className="msgMenuCstm">
                    <Badge
                        style={{ zIndex: 0 }}
                        overlap="rectangular"
                        badgeContent={messageCounter}
                        color="error"
                        variant="standard"
                    >
                        <img src={cstmMsgSvg} alt="" title="" />
                    </Badge>
                </MsgCustomBtn>
            </Tooltip>
        </MsgRowItem>
    );
}

export default HeaderChatNavButton;
